/**
 * Services the shell consumes through React context. The integrator wires the real
 * implementations (session module, repository-backed progress, catalog client); tests and the
 * dev fixture supply fakes. Screens never import session or persistence internals.
 */
import { createContext, useContext } from 'react';
import type { Puzzle } from '../engine/puzzle';
import type { ShareOutput } from '../data/share/portable';
import type { ReceiveResult } from '../data/share/receive';
import type { CatalogClient } from '../data/catalog/types';
import type { ResultRecord } from '../data/types';
import type { Difficulty, GameId } from '../engine/types';
import type { AnyGameModule } from '../games/registry';
import type { PwaHandle } from '../pwa/register';
import type { SessionController, CreateSessionOptions } from './session/types';

export type LevelStatus = 'new' | 'in-progress' | 'solved' | 'solved-with-help';

export interface ContinueItem {
  attemptId: string;
  puzzleId: string;
  gameId: GameId;
  difficulty: Difficulty;
  level?: { catalogVersion: string; levelNumber: number } | undefined;
  elapsedMs: number;
  updatedAt: number;
  /** 2C-7a: set from the attempt so a resume keeps its Daily tag. */
  mode?: 'level' | 'practice' | 'daily' | undefined;
  dailyDate?: string | undefined;
}

export interface LevelCount {
  solved: number;
  total: number;
}

export interface GameStats {
  gameId: GameId;
  solved: number;
  attempts: number;
  unassistedFirstAttempts: number;
  assistedCompletions: number;
  medianFirstAttemptMs: Partial<Record<Difficulty, number>>;
  /** Fastest unassisted result per puzzle ID (D6e). */
  personalBests: Record<string, number>;
}

/** Read-only progress queries (CCR: Repository has no result listing yet). */
export interface ProgressSource {
  listContinue(limit: number): Promise<ContinueItem[]>;
  /** D-31: preserved unfinished boards, with exact attempt IDs for reopening. */
  archived?(): Promise<ContinueItem[]>;
  levelStatuses(gameId: GameId, puzzleIds: string[]): Promise<Record<string, LevelStatus>>;
  stats(): Promise<GameStats[]>;
  /** Completed results, newest first (2C-2: streak, personal best, suggestions). Absent in fakes. */
  results?(): Promise<ResultRecord[]>;
  /**
   * Numbered catalog levels only (2C-7a): per game, how many catalog levels have a result (assisted
   * or not, the same rule as `levelStatuses`) and how many catalog entries are on this device.
   * Starter, Daily and practice boards never count. Absent in fakes.
   */
  levelCounts?(): Promise<Record<GameId, LevelCount>>;
  recent(limit: number): Promise<ContinueItem[]>;
  /** @deprecated D-31: automatic archive notices are no longer read or written. */
  archiveNotice?(): Promise<ArchiveNoticeView | null>;
  dismissArchiveNotice?(): Promise<void>;
}

export interface ArchiveNoticeView {
  gameId: GameId;
  difficulty: Difficulty;
  level?: { catalogVersion: string; levelNumber: number } | undefined;
  count: number;
}

/** D6e: favorites toggle for boards and levels. */
export interface FavoritesSource {
  has(puzzleId: string): Promise<boolean>;
  /** Returns the new state. */
  toggle(puzzleId: string): Promise<boolean>;
}

export interface SettingsSource {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
}

/** Clear all data (contract 1.8). D-58 removed export and import: nothing is downloaded or uploaded. */
export interface DataSource {
  clearAll(): Promise<void>;
}

export interface ShareSource {
  create(puzzle: Puzzle, origin: string): Promise<ShareOutput>;
  receive(text: string): Promise<ReceiveResult>;
}

export interface AppServices {
  share?: ShareSource;
  /** Absent in fakes: the Settings screen then shows data tools as unavailable. */
  data?: DataSource;
  favorites?: FavoritesSource;
  catalog: CatalogClient;
  progress: ProgressSource;
  settings: SettingsSource;
  /**
   * Safe point for applying a service worker update (M3 P5c): `idle()` is true when no puzzle
   * session is open; `settle()` resolves once every session's pending save has been written.
   */
  updateGate?: { idle(): boolean; settle(): Promise<void> };
  /** Creates a session controller (bound to the real deps by the integrator). */
  openSession(opts: CreateSessionOptions): Promise<SessionController<GameId>>;
  loadModule(id: GameId): Promise<AnyGameModule>;
  /** Service worker handle; absent in dev, tests and unsupported browsers. */
  pwa?: PwaHandle;
  /** Releases owned resources (worker). Call on app teardown. Optional for fakes. */
  dispose?(): void;
}

export const ServicesContext = createContext<AppServices | null>(null);

export function useServices(): AppServices {
  const s = useContext(ServicesContext);
  if (!s) throw new Error('ServicesContext is missing: wrap the app in <ServicesContext value={...}>');
  return s;
}

export function useOptionalServices(): AppServices | null {
  return useContext(ServicesContext);
}
